/**
 * ROOF CORE DEFINITION (2026-08-18, Matt — "roofs and ceilings treated the same way
 * as floors"): a roof TYPE declares its rafter family (Wood Rafters / TJI Rafters)
 * and size, and the assembly's core layer material + thickness DERIVE from it —
 * unifying the E2 round's `rafterSize` framing param with the assembly build-up (the
 * two were previously independent and could disagree; the drawn mass and the derived
 * members now share one declaration). Sheathing is separate from rafters: the
 * material is just 'Wood Rafter' (no '+ Sheathing') with roof decking as its own
 * substrate layer above the core where the assembly models one.
 *
 * The WOOD size picker reuses the E2 `rafterSize` key/choices (RAFTER_SIZES —
 * existing types keep their value); TJI reuses the floor family's sizes and its
 * 'TJI Joist' material (a TJI is a TJI). Unlike floors there is no concrete branch —
 * an exotic (concrete/other) roof simply stays UNDECLARED and untouched (the
 * conform/naming guard skips records with no raw coreType).
 */
import type { AssemblyLayer, MaterialId, ParamValue } from './types'
import { lumberSize, RAFTER_SIZES } from './roofFraming'
import { FLOOR_CORE_MATERIALS, TJI_JOIST_SIZES, TJI_WIDTH_IN } from './floorCore'
import { ROOF_TILE_MATERIAL_NAMES } from './roofTiles'

export const ROOF_CORE_TYPES = [
  { value: 'wood', label: 'Wood Rafters' },
  { value: 'tji', label: 'TJI Rafters' },
] as const
export type RoofCoreType = (typeof ROOF_CORE_TYPES)[number]['value']

export const ROOF_CORE_MATERIALS: Record<RoofCoreType, { name: string; color: string; category: string }> = {
  wood: { name: 'Wood Rafter', color: '#c9a86a', category: 'Wood' },
  tji: FLOOR_CORE_MATERIALS.tji!,
}

const coreTypeOf = (params: Record<string, ParamValue>): RoofCoreType =>
  String(params.coreType) === 'tji' ? 'tji' : 'wood'

/** DERIVED core thickness in feet — 4-decimal quantum (the wallCore convention) */
export function roofCoreThicknessFt(params: Record<string, ParamValue>): number {
  const depthIn = coreTypeOf(params) === 'tji'
    ? (TJI_JOIST_SIZES.find((s) => s.value === String(params.tjiRafterSize)) ?? TJI_JOIST_SIZES[1]).actualIn
    : (lumberSize(params.rafterSize, RAFTER_SIZES) ?? RAFTER_SIZES[2]).depthIn
  return Math.round((depthIn / 12) * 1e4) / 1e4
}

/** the core segment of the auto type name: '2×10 Wood Rafter' / '11⅞″ TJI' */
export function roofCoreLabel(params: Record<string, ParamValue>): string {
  if (coreTypeOf(params) === 'tji') {
    const s = TJI_JOIST_SIZES.find((x) => x.value === String(params.tjiRafterSize)) ?? TJI_JOIST_SIZES[1]
    return `${s.label} TJI`
  }
  const s = lumberSize(params.rafterSize, RAFTER_SIZES) ?? RAFTER_SIZES[2]
  return `${s.label} Wood Rafter`
}

/**
 * The derived rafter MEMBER cross-section for the framing generator — the one read
 * the E2 field/hip/ridge derivation sizes from. Wood: the curated lumber entry.
 * TJI: actual depth at the 2½″ flange width (representation-grade prisms — the I
 * profile is the same recorded deferral as floors).
 */
export function roofRafterMemberSize(params: Record<string, ParamValue>): { widthIn: number; depthIn: number } {
  if (coreTypeOf(params) === 'tji') {
    const s = TJI_JOIST_SIZES.find((x) => x.value === String(params.tjiRafterSize)) ?? TJI_JOIST_SIZES[1]
    return { widthIn: TJI_WIDTH_IN, depthIn: s.actualIn }
  }
  const s = lumberSize(params.rafterSize, RAFTER_SIZES) ?? RAFTER_SIZES[2]
  return { widthIn: s.widthIn, depthIn: s.depthIn }
}

/** Conform the first core layer to the declaration (the wallCore/floorCore contract).
 *  BARREL-TILE ROOFING (2026-08-19, Matt: "change the finish layer of the roofing to
 *  the actual roofing selection, instead of a monolithic layer"): the roof's TOP
 *  finish layer is ALSO driven by the declaration now — a `roofingProfile` of
 *  'barrel' conforms the exterior-most finish layer to the 'Clay Barrel Tile
 *  Roofing' material at 1/2″ (thin by design: the visible tile field DERIVES on top
 *  of the assembly surface, so a real tile thickness here would double-count the
 *  build-up). Profile '' leaves the finish layer alone (curated freely — clearing
 *  the profile deliberately does NOT revert, the user re-picks). */
export function conformRoofCoreLayers(
  params: Record<string, ParamValue>,
  layers: AssemblyLayer[],
  core: { start: number; end: number },
  materialIdByName: (name: string) => string | null,
): AssemblyLayer[] | null {
  if (!layers.length) return null
  const spec = ROOF_CORE_MATERIALS[coreTypeOf(params)]
  const coreIdx = Math.min(Math.max(core.start, 0), layers.length - 1)
  const matId = materialIdByName(spec.name)
  const thicknessFt = roofCoreThicknessFt(params)
  const cur = layers[coreIdx]
  const nextMat = (matId ?? cur.materialId) as MaterialId
  let next: AssemblyLayer[] | null = null
  if (cur.materialId !== nextMat || Math.abs(cur.thicknessFt - thicknessFt) >= 1e-9) {
    next = layers.map((l, i) => (i === coreIdx ? { ...l, materialId: nextMat, thicknessFt } : l))
  }
  // PAINTED ROOFING (2026-09-28): roofing is painted per plane now — the retired type
  // profile no longer drives a finish layer
  if (false && params.roofingProfile === 'barrel' && coreIdx > 0 && layers[0].function === 'finish') {
    const roofMatId = materialIdByName(ROOF_TILE_MATERIAL_NAMES.layer)
    const base = next ?? layers
    const cur0 = base[0]
    const next0Mat = (roofMatId ?? cur0.materialId) as MaterialId
    const ROOFING_LAYER_FT = 0.0417   // 1/2″ — underlayment + nominal flat build
    if (cur0.materialId !== next0Mat || Math.abs(cur0.thicknessFt - ROOFING_LAYER_FT) >= 1e-9) {
      next = base.map((l, i) => (i === 0 ? { ...l, materialId: next0Mat, thicknessFt: ROOFING_LAYER_FT } : l))
    }
  }
  return next
}

/** Is the roof's top finish layer locked to the Roofing declaration? (the LayerTable
 *  lock — must stay in lockstep with the conform above) */
export function roofingFinishLocked(_category: string, _params: Record<string, ParamValue>): boolean {
  // PAINTED ROOFING: nothing in a roof assembly is driven by a roofing profile anymore
  return false
}
